import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";

/**
 * Manages per-user watch progress for talks. Backed by /api/me/watch.
 *
 *   - On mount: fetches the current {session_id: {status}} map.
 *   - mark(sessionId, status): optimistic update + POST to backend.
 *
 * Server enforces "no-downgrade": once a session is `watched`, later
 * `in_progress` writes are ignored. Client mirrors this to keep the UI in sync.
 */
export default function useWatchProgress() {
  const [items, setItems] = useState({}); // { session_id: { status, updated_at } }
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/me/watch");
        if (alive) setItems(r.data?.items || {});
      } catch {
        if (alive) setItems({});
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const mark = useCallback(async (sessionId, status) => {
    if (!sessionId || !status) return;
    // No-downgrade guard on the client.
    setItems((prev) => {
      const current = prev[sessionId]?.status;
      if (current === "watched" && status === "in_progress") return prev;
      return {
        ...prev,
        [sessionId]: { status, updated_at: new Date().toISOString() },
      };
    });
    try {
      const r = await api.post(`/me/watch/${encodeURIComponent(sessionId)}`, { status });
      const serverStatus = r.data?.status || status;
      setItems((prev) => ({
        ...prev,
        [sessionId]: { status: serverStatus, updated_at: new Date().toISOString() },
      }));
    } catch {
      // Best-effort. Silent failure leaves the optimistic value in place; next
      // page load reconciles from the server.
    }
  }, []);

  return { items, mark, loaded };
}
